import * as React from "react";
import {
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  CircleHelp,
  CircleSlash,
  CircleX,
  Clock,
  Expand,
  Gauge,
  History,
  ImageOff,
  Info,
  OctagonX,
  PenLine,
  Sparkles,
  TriangleAlert,
  Zap,
} from "lucide-react";
import {
  ANALYSIS_MAX_RETRIES,
  SEVERITY_META,
  analysisFailureSentence,
  attemptHistory,
  checklistAnswers,
  compareSeverityDesc,
  confidenceLabel,
  dispositionCopy,
  effectiveSeverity,
  isAnswered,
  isDismissed,
  isSeverityOverridden,
  isStopWork,
  captureQualitySchema,
  keyFrameSchema,
  latestAttempt,
  latestDetections,
  latestFindings,
  moduleOrder,
  utilityMetadata,
  type AnalysisStatus,
  type AssessmentModuleCode,
  type AttemptEntry,
  type CaptureQuality,
  type CaptureQualityReason,
  type ChecklistAnswer,
  type ChecklistDetectionAnswer,
  type DetectionState,
  type AssessmentWithContext,
  type Finding,
  type SeverityTier,
} from "@taskiq/shared";
import { Badge, SEVERITY_ICONS, SeverityChip } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { IconButton } from "@/components/ui/IconButton";
import { InfoHint } from "@/components/ui/InfoHint";
import { Modal } from "@/components/ui/Modal";
import { AngleGauge } from "@/components/charts/AngleGauge";
import { formatDayTimeIn, formatTimeIn } from "@/lib/dates";
import { cn } from "@/lib/cn";
import {
  MediaGallery,
  MediaLightbox,
  MediaTile,
  viewerItems,
  type ViewerItem,
} from "./MediaViewer";
import { ModuleIcon } from "./ModuleIcon";

/**
 * The pre-task assessment, as the record a supervisor reviews.
 *
 * **One card per module, showing its newest run.** A visit is N modules and a
 * re-run is another `assessments` row rather than an overwrite, so a ladder
 * that moved twice is three rows of one check. Rendering all three as peers
 * made a four-module visit read as an eight-check visit; they are grouped now,
 * newest on the card and the rest behind the header's history control. Nothing
 * is dropped - the count is on the control, because "they ran it four times" is
 * itself something a reviewer came to see.
 *
 * ── One history control, in the same place on every card ───────────────────
 *
 * A check is superseded two ways: a re-run writes another `assessments` row,
 * and a re-shoot replaces the media on the row already there. Those were two
 * controls, worded and placed differently - "View previous attempts" in the
 * card's footer, "Earlier submissions" under the photographs - so which one a
 * card offered depended on which module it was, and a reviewer had to know the
 * mechanism to find the answer to one question. They are one glyph in the card
 * header now, on every module, carrying the count of everything behind it.
 *
 * ── The history is a dialog, not a drawer inside the card ──────────────────
 *
 * An attempt is a full record: an angle, a checklist, a set of photographs,
 * findings and what was done about them, and none of that fits under the card
 * it is superseding without pushing the current record off the screen. So the
 * control opens a dialog, and each attempt in it renders exactly what the card
 * renders. Same on the phone, where it is a sheet.
 *
 * ── Labels come from the catalogue, not from here ───────────────────────────
 *
 * A PPE item and a ladder check carry their labels in
 * `assessment_modules.config`, so this renders `config` and never a list of its
 * own. That is the point of the column: an administrator can add an item without
 * a console release, and a record written before they did still reads back with
 * the codes it was answered against.
 *
 * ── Every card carries figures, not restatements ────────────────────────────
 *
 * There is no line explaining what "Advisory" means. The chip carries the word,
 * the icon and the colour; the space goes to the control that was actually put
 * in place, which is the part an auditor asks about.
 */

interface ModuleConfig {
  code: string;
  config: Record<string, unknown>;
  /** The catalogue's one-liner, shown on a card with nothing recorded yet. */
  blurb: string | null;
}

interface PanelProps {
  assessments: AssessmentWithContext[];
  /** The catalogue, for the checklist labels. Missing is survivable, not fatal. */
  modules: ModuleConfig[];
  timezone: string;
}

/** One module of the visit and every run of it, newest first. */
interface ModuleGroup {
  key: string;
  code: string;
  tries: AssessmentWithContext[];
}

export function AssessmentPanel({ assessments, modules, timezone }: PanelProps) {
  const configs = React.useMemo(
    () => new Map(modules.map((module) => [module.code, module.config])),
    [modules]
  );

  // The catalogue's own one-liner per module, which is what a card falls back
  // to when the check has nothing recorded on it yet. From the live row rather
  // than from `ASSESSMENT_MODULE_SEED`: bootstrap only inserts missing
  // catalogue rows and never rewrites one an administrator may since have
  // edited, so the seed is the value this shipped with and the table is the
  // value in force.
  const blurbs = React.useMemo(
    () => new Map(modules.map((module) => [module.code, module.blurb])),
    [modules]
  );

  // One entry per module, in the order the flow runs them.
  //
  // A re-run writes another `assessments` row rather than overwriting the
  // first, so a ladder that moved twice is three rows of ONE check - not three
  // checks. The card renders the newest, because that is what the technician
  // stood behind when they closed the visit; the earlier runs are still the
  // record and are one press away rather than eight cards of scrolling.
  const groups = React.useMemo<ModuleGroup[]>(() => {
    const byModule = new Map<string, AssessmentWithContext[]>();
    for (const assessment of assessments) {
      const key = assessment.module_id || assessment.module?.code || assessment.id;
      const tries = byModule.get(key);
      if (tries) tries.push(assessment);
      else byModule.set(key, [assessment]);
    }

    return [...byModule.entries()]
      .map(([key, tries]) => ({
        key,
        code: tries[0]!.module?.code ?? "",
        tries: [...tries].sort((a, b) => b.created_at.localeCompare(a.created_at)),
      }))
      .sort((a, b) => moduleOrder(a.code) - moduleOrder(b.code));
  }, [assessments]);

  // The demo-era layout, kept: consecutive ladder and PPE cards share a row
  // on wide screens (`assessment__pair`) because each is half detail and they
  // read best compared. A lone one, or any other module, takes the full width.
  const rows: ModuleGroup[][] = [];
  for (const group of groups) {
    const pairable = group.code === "heights" || group.code === "ppe";
    const last = rows[rows.length - 1];
    if (pairable && last && last.length === 1) {
      const lastCode = last[0]!.code;
      if (lastCode === "heights" || lastCode === "ppe") {
        last.push(group);
        continue;
      }
    }
    rows.push([group]);
  }

  return (
    <div className="assessment">
      {rows.map((row) =>
        row.length === 2 ? (
          <div key={row[0]!.key} className="assessment__pair">
            {row.map((group) => (
              <ModuleGroupCard
                key={group.key}
                group={group}
                config={configs.get(group.code) ?? {}}
                blurb={blurbs.get(group.code) ?? null}
                timezone={timezone}
              />
            ))}
          </div>
        ) : (
          <ModuleGroupCard
            key={row[0]!.key}
            group={row[0]!}
            config={configs.get(row[0]!.code) ?? {}}
            blurb={blurbs.get(row[0]!.code) ?? null}
            timezone={timezone}
          />
        )
      )}
    </div>
  );
}

/**
 * The newest run of one module, with every earlier version a press away.
 *
 * The control is `IconButton` in the card's own header, in the same place on
 * every module - the console's one square icon control, so it sits at the size
 * and hovers the way the icon buttons on every other screen do.
 */
function ModuleGroupCard({
  group,
  config,
  blurb,
  timezone,
}: {
  group: ModuleGroup;
  config: Record<string, unknown>;
  /** The catalogue's description, for a check with nothing recorded on it yet. */
  blurb: string | null;
  timezone: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [latest, ...earlier] = group.tries;

  if (!latest) return null;

  const view = moduleView(latest, config, timezone, blurb);
  // Both ways a check gets superseded, counted as one thing. A re-run wrote
  // another `assessments` row; a re-shoot replaced the media on one already
  // there - and either row can carry its own reshoots, not just the newest.
  // The reviewer's question is "what did this say before", and which
  // mechanism answered it is not something they should have to know to ask.
  const versions =
    earlier.length + group.tries.reduce((sum, t) => sum + replacedAttempts(t).length, 0);

  const code = latest.module?.code;
  // Green once the check has a finished attempt, which is the same rule the
  // phone's `ModuleRow` tints its tile by.
  const finished = group.tries.some((t) => t.status === "completed" || t.status === "skipped");

  return (
    <Card fill>
      <CardHeader
        icon={<ModuleIcon code={code} tone={finished ? "ok" : "neutral"} />}
        title={
          <span className="assessment__title">
            <span className="assessment__title-text">{view.title}</span>
          </span>
        }
        subtitle={view.subtitle}
        actions={
          <>
            {view.actions}
            {versions > 0 && (
              <IconButton label="Version history" size="sm" onClick={() => setOpen(true)}>
                <History aria-hidden />
              </IconButton>
            )}
          </>
        }
      />
      <CardBody className={view.bodyClass}>{view.content}</CardBody>

      {open && (
        <HistoryModal
          title={view.title}
          tries={group.tries}
          config={config}
          blurb={blurb}
          timezone={timezone}
          onClose={() => setOpen(false)}
        />
      )}
    </Card>
  );
}

/**
 * One entry in a check's history: either a real run (its own `assessments`
 * row, full record) or a reshoot (photos/video only - the checklist judgement
 * they were taken against belongs to the run they were submitted under).
 *
 * Both kinds are numbered into one "Attempt N of M" sequence, oldest to
 * newest, because a reviewer's question - "what did this say before" - does
 * not care which mechanism produced an earlier version. A reshoot's body is
 * just its media, which is what tells the two kinds apart on the page without
 * a second label doing it in words.
 */
type HistoryEntry =
  | { kind: "run"; assessment: AssessmentWithContext; view: ModuleView; when: string }
  | { kind: "reshoot"; assessment: AssessmentWithContext; attempt: number; when: string };

/**
 * Earlier submissions on one row that a later one superseded - a reshoot, not
 * a new `assessments` row. Any evidentiary file (never the thumbnail alone)
 * with a lower attempt number than the one this record's answers were taken
 * against; not filtered to one media kind, so it covers a sweep's video the
 * same way it covers PPE's photographs. Newest reshoot first.
 */
function replacedAttempts(
  assessment: AssessmentWithContext,
  statedAttempt?: number
): { attempt: number; createdAt: string }[] {
  const submitted = submittedAttempt(assessment, statedAttempt);
  const earliestByAttempt = new Map<number, string>();
  for (const item of assessment.media.items) {
    if (item.kind === "thumbnail" || item.attempt >= submitted) continue;
    const seen = earliestByAttempt.get(item.attempt);
    if (!seen || item.created_at < seen) earliestByAttempt.set(item.attempt, item.created_at);
  }
  return [...earliestByAttempt.entries()]
    .map(([attempt, createdAt]) => ({ attempt, createdAt }))
    .sort((a, b) => b.attempt - a.attempt);
}

/**
 * Every version of one check, in full, newest first.
 *
 * The newest is in the list and labelled rather than left out. "What was
 * different last time" is a comparison, and a dialog that shows only the runs
 * being compared *against* makes the reader hold the current one in their head
 * while they scroll.
 *
 * The title is the check's name and nothing else. It used to carry the tally
 * too - "Working at heights · 2 attempts" - which every entry underneath then
 * said again, one of them per row.
 */
function HistoryModal({
  title,
  tries,
  config,
  blurb,
  timezone,
  onClose,
}: {
  title: string;
  tries: AssessmentWithContext[];
  config: Record<string, unknown>;
  blurb: string | null;
  timezone: string;
  onClose: () => void;
}) {
  // `tries` is newest row first. Each row's own reshoots (older than what it
  // submitted) slot in right after it, newest reshoot first, so the whole
  // list stays newest-to-oldest without needing a second sort by timestamp -
  // a reshoot carries no `assessments` row and so no `completed_at` to sort by.
  const entries: HistoryEntry[] = [];
  for (const assessment of tries) {
    const view = moduleView(assessment, config, timezone, blurb);
    entries.push({
      kind: "run",
      assessment,
      view,
      when: assessment.completed_at ?? assessment.created_at,
    });
    for (const reshoot of replacedAttempts(assessment)) {
      entries.push({
        kind: "reshoot",
        assessment,
        attempt: reshoot.attempt,
        when: reshoot.createdAt,
      });
    }
  }
  const many = entries.length > 1;

  return (
    <Modal open onClose={onClose} title={title} className="attempts-modal">
      <ol className="attempt-list">
        {entries.map((entry, index) => {
          const ordinal = entries.length - index;
          const key =
            entry.kind === "run" ? entry.assessment.id : `${entry.assessment.id}-${entry.attempt}`;
          return (
            <li key={key} className="attempt-entry">
              <div className="attempt-entry__head">
                <p className="attempt-entry__label">
                  {/* Numbered from the oldest, so "attempt 1" is the first
                      version whichever way the list happens to be sorted. */}
                  {many ? `Attempt ${ordinal} of ${entries.length}` : "On the record"}
                </p>
                {index === 0 && many && (
                  <Badge tone="accent" icon={<CircleCheck aria-hidden />}>
                    On the record
                  </Badge>
                )}
                <p className="attempt-entry__when tnum">{formatDayTimeIn(entry.when, timezone)}</p>
                {/* A reshoot has no *outcome* of its own - pass or fail belongs
                    to the run that closed the check. It does have an analysis,
                    which is the block below. */}
                {entry.kind === "run" && <StatusBadge assessment={entry.assessment} />}
              </div>

              {entry.kind === "run" ? (
                <div className={entry.view.bodyClass}>{entry.view.content}</div>
              ) : (
                <AttemptRecord
                  assessment={entry.assessment}
                  attempt={entry.attempt}
                  config={config}
                  title={title}
                  timezone={timezone}
                />
              )}
            </li>
          );
        })}
      </ol>
    </Modal>
  );
}

/* ─────────────────────────── one module, rendered ───────────────────────── */

/**
 * A module's header and body, as data rather than as a card.
 *
 * The card and the attempt dialog both need exactly this, and the only
 * alternative - a `card` prop threaded through five components - is how the two
 * end up rendering different amounts of the same record.
 */
interface ModuleView {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  /** The class that lays the body out. Applied by both the card and the dialog. */
  bodyClass?: string;
  content: React.ReactNode;
}

/**
 * One superseded submission, rendered as the check it is a version of.
 *
 * ── Why this is not its own layout ─────────────────────────────────────────
 *
 * It used to be: a strip of photographs and a bare list of detections, with no
 * heading, no tally, and only the rows the detector had been asked about. Set
 * next to the current attempt - a full check card - the two read as two
 * different records of two different things, which is the opposite of what a
 * version history is for. A reviewer opens this dialog to answer "what changed
 * between them", and that question needs both attempts drawn the same way.
 *
 * So this renders the module's own shell: the ladder's gauge over its
 * photographs beside its five set-up checks, PPE's equipment list beside its
 * frames, the area scan's hazards beside its clip. Same components, same
 * headings, same spacing as the live card.
 *
 * ── What it may say, and what it may not ───────────────────────────────────
 *
 * **These are the detector's readings, not the technician's answers.** A
 * superseded submission has no ticks: the technician answered against the
 * photographs that ended up on the record, and the ones before them were
 * replaced before anybody was asked. So every row says "Seen", "Not seen", "Not
 * in shot" or "Not asked" - never "Confirmed by user" - and the note under the
 * list says so in a sentence.
 *
 * **Read-only, and no acknowledgement anywhere.** An acknowledgement belongs to
 * the attempt that produced the finding it answers, which is why the area scan
 * keeps its findings per attempt rather than moving them forward.
 */
function AttemptRecord({
  assessment,
  attempt,
  config,
  title,
  timezone,
}: {
  assessment: AssessmentWithContext;
  attempt: number;
  config: Record<string, unknown>;
  title: string;
  timezone: string;
}) {
  const entry = attemptHistory(assessment.metadata).find((item) => item.attempt === attempt);
  // The check's own answers, for the codes no detector is ever asked about -
  // see `attested` on `DetectionList`. Read off the assessment rather than the
  // attempt because that is where they live: one answer per code for the whole
  // check, not one per submission.
  const claims = new Map(
    readAnswers(
      (assessment.metadata as Record<string, unknown>)[
        assessment.module?.code === "ppe" ? "items" : "checks"
      ]
    ).map((answer) => [answer.code, answer.present])
  );
  // This attempt's frames, not the record's. A superseded submission's findings
  // were read off the footage that was replaced, and showing them beside the
  // current still would caption them with a work area that had already changed.
  const items = viewerItems(assessment.media, attempt);
  const media = (
    <MediaGallery
      compact={assessment.module?.code === "heights"}
      items={items}
      title={title}
      placeholder="This submission's media is no longer available"
    />
  );

  // Media submitted before this shipped, or before the analysis ran on it.
  // Saying so is better than an empty block that reads as "found nothing".
  const none = (
    <p className="muted attempt-entry__none">No analysis was recorded for this submission.</p>
  );

  switch (assessment.module?.code) {
    case "heights":
      return (
        <div className="checkcard checkcard--heights">
          <div className="checkcard__media heights__panel">
            {/* The angle this set-up was measured at, not the one on the
                record. A re-shoot is usually a ladder that moved, so pairing
                these photographs with the reading the technician finished on
                would show a set-up that never existed. */}
            {entry?.reading && (
              <AngleReading
                angle={entry.reading.angle_deg}
                target={Number(config.target_angle_deg ?? 75.5)}
                tolerance={Number(config.tolerance_deg ?? 2)}
                advisory={entry.angle_action ?? null}
              />
            )}
            {media}
          </div>
          <div className="checkcard__detail">
            {entry ? (
              <DetectionList
                entry={entry}
                catalogue={checklistFrom(config, "checks")}
                label="Set-up checks"
                attested={claims}
              />
            ) : (
              none
            )}
          </div>
        </div>
      );

    case "ppe":
      return (
        <div className="checkcard checkcard--ppe">
          <div className="checkcard__media">{media}</div>
          <div className="checkcard__detail">
            {entry ? (
              <DetectionList
                entry={entry}
                catalogue={checklistFrom(config, "items")}
                label="Protective equipment"
                split
                attested={claims}
              />
            ) : (
              none
            )}
          </div>
        </div>
      );

    case "area_scan": {
      // Sorted by the severity each is FILED at, so a hazard the technician
      // refused to work under leads the list even where the analysis was
      // relaxed about it - the chip beside it already says `stop`, and a list
      // ordered by a different number than the one it displays reads as broken.
      const findings = [...(entry?.findings ?? [])].sort(compareFindings);
      const scan = scanMedia(assessment, attempt, entry);
      const seenIn = findings.map((finding) =>
        framesFor(finding, assessment.media, entry?.attempt, scan.frames)
      );
      return (
        <div className="checkcard checkcard--scan">
          <div className="checkcard__media">
            <MediaGallery
              items={scan.clip}
              title={title}
              placeholder="This submission's media is no longer available"
            />
          </div>
          <div className="checkcard__detail">
            <SweepQualityNote quality={readQuality(entry)} />
            {!entry ? (
              none
            ) : findings.length === 0 ? (
              // Only analysed sweeps have entries (decisions #52), so an empty
              // one is a model that looked and found nothing.
              <p className="muted attempt-entry__none">This sweep raised no hazards.</p>
            ) : (
              <>
                <p className="checkcard__detail-label">
                  Hazards raised
                  <span className="checkcard__detail-count tnum">{findings.length}</span>
                </p>
                <ul className="scan-list">
                  {findings.map((finding, index) => (
                    <FindingRow
                      key={finding.id}
                      finding={finding}
                      index={index}
                      media={scan.all}
                      frames={seenIn[index]}
                      timezone={timezone}
                    />
                  ))}
                </ul>
              </>
            )}
          </div>
          <KeyFrameStrip
            frames={scan.frames}
            hazards={hazardSpans(findings, seenIn)}
            durationMs={readDurationMs(entry)}
            title={title}
          />
        </div>
      );
    }

    default:
      return (
        <div className="checkcard">
          <div className="checkcard__media">{media}</div>
          <div className="checkcard__detail">{none}</div>
        </div>
      );
  }
}

/**
 * What one submission's photographs were read to say, row by row.
 *
 * Every code in the module's catalogue, in the catalogue's own order - not just
 * the ones that came back. `ground_firm` is the case that matters: the detector
 * is never asked about it (`config.attested`), so it has no detection and used
 * to vanish from this list entirely, leaving a four-row history of a five-row
 * check. It is here, marked "Not asked", which is the honest reading of a row
 * nothing looked at.
 */
function DetectionList({
  entry,
  catalogue,
  label,
  split,
  attested,
}: {
  entry: AttemptEntry;
  catalogue: { code: string; label: string }[];
  label: string;
  split?: boolean;
  /**
   * The technician's own answers to the codes no detector is ever asked about -
   * the ladder's `ground_firm`. Keyed by code, and empty for a module that has
   * none.
   *
   * **The one thing on this list a person said**, and the exception is narrow on
   * purpose. Every other row here is the reading of photographs that were
   * replaced before anybody was asked to confirm them, so it may not carry a
   * tick. An attested code has no reading to be superseded: nothing optical
   * measures soil bearing capacity, the answer is a claim about the check rather
   * than about one submission of it, and it is the same claim on every attempt.
   * Marked "Not asked" it was the opposite of the truth - the technician had
   * answered it, and the record said nobody had.
   */
  attested?: Map<string, boolean>;
}) {
  const detections = entry.detections ?? [];
  const byCode = new Map(detections.map((detection) => [detection.code, detection]));
  // What the detector was actually handed, which is the module's list minus its
  // attested codes. Recorded per attempt precisely so this stays answerable
  // after an administrator edits the config.
  const asked = new Set(
    Array.isArray(entry.input?.asked) ? (entry.input.asked as unknown[]).map(String) : []
  );

  const rows: CheckRow[] = catalogue.map((item) => {
    const detection = byCode.get(item.code);
    const claim = attested?.get(item.code);
    if (!detection && claim !== undefined) {
      // No `detected` and no `confidence`: nothing looked at it, which is what
      // `checkBasis` reads as the technician's own word.
      return { ...item, answer: { present: claim } };
    }
    return {
      ...item,
      // What the model wrote about this row, behind the row's own control.
      detail: detection,
      answer: {
        // The detector's reading, never a tick. `CheckList` draws its glyph off
        // this, and the basis line beside it says where it came from.
        present: detection?.detected === "present",
        detected: detection?.detected ?? "not_assessed",
        confidence: detection?.confidence ?? null,
      },
    };
  });

  const seen = rows.filter((row) => row.answer?.present).length;

  return (
    <>
      <p className="checkcard__detail-label">
        {label}
        <span className="checkcard__detail-count tnum">
          {seen} of {rows.length}
        </span>
      </p>

      <CheckList rows={rows} split={split} ticked={false} basisOf={detectorBasis(asked)} />
    </>
  );
}

/**
 * Where a superseded row's answer came from, which is always a machine.
 *
 * Deliberately not `checkBasis`: that one can say "Confirmed by user", and on a
 * submission nobody was asked about, no user confirmed anything.
 */
function detectorBasis(asked: Set<string>) {
  return (row: CheckRow): RowBasis | null => {
    const answer = row.answer;

    // An answer with no `detected` at all: nothing read this row, and what is on
    // it is the technician's own word. `DetectionList` builds exactly one kind
    // of row this way - an attested code the detector is never handed, which
    // they answered - and every other row it builds carries a `detected` even
    // when that is `not_assessed`. See `attested` on `DetectionList` for why
    // this one may speak for a person where the rest of the list may not.
    if (answer && answer.detected === undefined) {
      return answer.present
        ? {
            text: "By technician",
            source: "hand",
            detail: "No detector is ever asked about this row. The technician confirmed it.",
          }
        : {
            text: "Not confirmed",
            source: "none",
            detail:
              "No detector is ever asked about this row, and the technician did not confirm it.",
          };
    }

    // A row the detector was never handed and nobody answered either. Not "not
    // in shot", which claims a photograph was taken and did not cover it.
    if (asked.size > 0 && !asked.has(row.code)) {
      return {
        text: "Not asked",
        source: "none",
        detail: "The detector was never asked about this row.",
      };
    }

    if (!answer || answer.detected === "not_assessed") {
      return {
        text: "Not in shot",
        source: "none",
        detail: "No photograph in this submission covered it.",
      };
    }

    const sure = confidenceNote(answer.confidence);
    return answer.detected === "present"
      ? { text: "Found", source: "ai", detail: `The detector found this.${sure}` }
      : {
          text: "Not there",
          source: "ai",
          detail: `The detector could see where this would be and it was not there.${sure}`,
        };
  };
}

/**
 * A card's two lines, with the guarantee that the second one exists.
 *
 * ── Why the fallback is here and not in each view ───────────────────────────
 *
 * Every per-module view writes a better subtitle than the catalogue's blurb -
 * the ladder's names the OSHA rule its target expresses, and it can, because
 * the target comes from the catalogue rather than from the record. But all
 * three of those views fall through to [plainView] the moment nothing has been
 * submitted, and `plainView` only had a subtitle for an *advisory* module.
 *
 * So a check that was **not started, or in progress** lost its second line
 * altogether: the two states a supervisor is most likely to be reading while a
 * technician is still on site were the two with the least on the card, and the
 * title was left sitting alone against an icon and a badge.
 *
 * One place fills the gap, and the views stay free to say something better.
 */
function moduleView(
  assessment: AssessmentWithContext,
  config: Record<string, unknown>,
  timezone: string,
  blurb: string | null
): ModuleView {
  const view = moduleViewFor(assessment, config, timezone);
  if (view.subtitle) return view;
  return { ...view, subtitle: blurb ?? undefined };
}

function moduleViewFor(
  assessment: AssessmentWithContext,
  config: Record<string, unknown>,
  timezone: string
): ModuleView {
  switch (assessment.module?.code as AssessmentModuleCode | undefined) {
    case "area_scan":
      return scanView(assessment, timezone);
    case "heights":
      return heightsView(assessment, config);
    case "ppe":
      return ppeView(assessment, config);
    case "utility":
      return utilityView(assessment);
    default:
      // A module added to the catalogue after this shipped. Its outcome and
      // notes still read back, which is more useful than a blank space.
      return plainView(assessment);
  }
}

/* ─────────────────────────────── shared bits ───────────────────────────── */

/**
 * How the check went, and the **only** badge any of these cards carries.
 *
 * Every module used to derive its own headline from its own metadata: the
 * ladder card badged the angle ("Within tolerance") and ignored the two set-up
 * checks nobody could confirm, PPE named a missing item, the area scan counted
 * open findings. Three rules, none of them the row's own `outcome`, and the
 * handset showing a fourth answer beside them - the ladder read "Failed" there
 * at the same moment.
 *
 * So the verdict is `assessments.outcome`, the API derives it from what the
 * check recorded (`assessmentOutcome` in `@taskiq/shared`), and both clients
 * render it. The module-specific detail did not go anywhere: the deviation is
 * under the gauge in degrees, the missing items are the unticked rows of the
 * list, the open findings are the tally over them. What is gone is three
 * headlines that could each contradict the record they sat on.
 */
function StatusBadge({ assessment }: { assessment: AssessmentWithContext }) {
  if (assessment.status === "skipped") {
    return (
      <Badge tone="neutral" icon={<CircleSlash aria-hidden />}>
        Skipped
      </Badge>
    );
  }
  if (assessment.status !== "completed") {
    return (
      <Badge tone="accent" icon={<Clock aria-hidden />}>
        {assessment.status === "in_progress" ? "In progress" : "Not started"}
      </Badge>
    );
  }
  if (assessment.outcome === "fail") {
    return (
      <Badge tone="stop" icon={<CircleX aria-hidden />}>
        Failed
      </Badge>
    );
  }
  if (assessment.outcome === "attention") {
    // Not a ruler. That icon was here from when this badge was the ladder's,
    // and a measuring rule over a PPE card says nothing about a missing glove.
    return (
      <Badge tone="caution" icon={<TriangleAlert aria-hidden />}>
        Needs attention
      </Badge>
    );
  }
  return (
    <Badge tone="ok" icon={<CircleCheck aria-hidden />}>
      Passed
    </Badge>
  );
}

/** `{ code, label }` entries out of a module's config, in the order it lists them. */
function checklistFrom(config: Record<string, unknown>, key: "items" | "checks") {
  const raw = config[key];
  if (!Array.isArray(raw)) return [] as { code: string; label: string }[];
  return raw.flatMap((entry) =>
    entry && typeof entry === "object" && "code" in entry
      ? [
          {
            code: String(entry.code),
            label: String((entry as { label?: unknown }).label ?? entry.code),
          },
        ]
      : []
  );
}

function title(assessment: AssessmentWithContext) {
  return assessment.module?.label ?? "Check";
}

/**
 * Which submission this record's answers were written against.
 *
 * The metadata says so where the module writes it down; otherwise it is the
 * highest attempt on the media, which is what a re-check leaves behind. Not
 * `items.at(-1)`: the array's order is the API's, and the last row of it is
 * whatever kind happened to sort last rather than the newest submission.
 */
function submittedAttempt(assessment: AssessmentWithContext, stated?: number) {
  if (stated) return stated;
  const attempts = assessment.media.items.map((item) => item.attempt);
  return attempts.length > 0 ? Math.max(...attempts) : 1;
}

/* ───────────────────────────────── area scan ───────────────────────────── */

/*
 * There is no model or detection line on this card.
 *
 * `model`, `model_version` and `detected_at` are on every finding and they are
 * still in the API's response and in the export - a record has to be able to
 * say what analysed it. What a supervisor reading a visit needs is when the
 * work started and when it finished, which the visit's own header carries; a
 * detector's build number under every hazard is a fact for whoever is auditing
 * the model, not for whoever is auditing the job.
 */

function scanView(assessment: AssessmentWithContext, timezone: string): ModuleView {
  // Where the newest sweep's analysis stands. `metadata.attempts` only holds
  // answers, so while a newer sweep is being checked - or could not be - the
  // newest findings there are an older sweep's, of a place since re-shot.
  const analysis = assessment.analysis;
  const current = !analysis || analysis.state === "analysed";
  const findings = [...(current ? latestFindings(assessment.metadata) : [])].sort(compareFindings);
  const open = findings.filter((finding) => !finding.acknowledged_at);
  const latest = latestAttempt(assessment.metadata);
  const attempt = submittedAttempt(assessment);
  // The analysis of the sweep on the card, for its frame times and the phone's
  // verdict. The same entry as `latest` except in the moment between a re-shot
  // sweep landing and its analysis coming back.
  const entry = attemptHistory(assessment.metadata).find((item) => item.attempt === attempt);
  const scan = scanMedia(assessment, attempt, entry);
  const seenIn = findings.map((finding) =>
    framesFor(finding, assessment.media, latest?.attempt, scan.frames)
  );

  const acknowledged = findings.length - open.length;
  // Nobody has looked at the newest sweep yet, or nobody could. An empty list
  // under it must never read as the clean result an empty list otherwise is.
  const unread = current ? null : analysis;

  return {
    title: title(assessment),
    // What the check *is*, the way the ladder card names its target and the PPE
    // card names its list. The tally moved to a heading over the findings
    // themselves - see `.checkcard__detail-label` - which is where the other two
    // cards already put theirs, and where it sits over the column it counts.
    subtitle: "A sweep of the work area, analysed for hazards",
    // The open tally is the heading over the findings themselves - "Hazards
    // acknowledged, 2 of 3" - which is where it belongs and where it does not
    // read as a second, competing verdict beside the outcome.
    actions: <StatusBadge assessment={assessment} />,
    // The same two-column shape as the ladder and PPE cards, and it holds
    // whether or not there is media. An absent clip used to render nothing,
    // which let the findings reflow across the whole card - one per row, each
    // with six stacked lines, on a record whose whole point is to be scanned.
    bodyClass: "checkcard checkcard--scan",
    content: (
      <>
        {/* A column held open by a frame that says "nothing here yet" is a
            column spending a third of the card on a fact the status badge
            already carries. It only holds itself open once the check is
            closed, where an absent clip is a finding about the record rather
            than a stage it has not reached: the findings take the full width
            until then. */}
        {(scan.all.length > 0 || assessment.status === "completed") && (
          <div className="checkcard__media">
            <MediaGallery
              items={scan.clip}
              title={title(assessment)}
              placeholder="No footage was uploaded"
            />
          </div>
        )}

        <div className="checkcard__detail">
          <SweepQualityNote quality={readQuality(entry)} />

          {findings.length > 0 && (
            <p className="checkcard__detail-label">
              Hazards acknowledged
              <span className="checkcard__detail-count tnum">
                {acknowledged} of {findings.length}
              </span>
            </p>
          )}

          {findings.length > 0 ? (
            <ul className="scan-list">
              {findings.map((finding, index) => (
                <FindingRow
                  key={finding.id}
                  finding={finding}
                  index={index}
                  media={scan.all}
                  frames={seenIn[index]}
                  timezone={timezone}
                />
              ))}
            </ul>
          ) : unread ? (
            <AnalysisUnavailable status={unread} />
          ) : (
            <p className="muted">
              {assessment.status !== "completed"
                ? "Findings appear here once the sweep is uploaded and analysed."
                : latest
                  ? "The sweep raised no hazards. That is a result, not a gap - it goes on the record the same way."
                  : // Closed with no analysis on the record at all. Saying "no
                    // hazards" here would be claiming a result nobody produced.
                    "No analysis was recorded for this sweep."}
            </p>
          )}
        </div>

        <KeyFrameStrip
          frames={scan.frames}
          hazards={hazardSpans(findings, seenIn)}
          durationMs={readDurationMs(entry)}
          title={title(assessment)}
        />
      </>
    ),
  };
}

/**
 * A key frame, and where in the clip it was cut from. The server cannot prove a
 * frame came from the video, so the time is what lets a reviewer check.
 */
type KeyFrameItem = ViewerItem & { at: string | null; ms: number | null };

/**
 * The sweep's evidence, split the way the card draws it.
 *
 * The phone cuts the sweep into a handful of key frames and uploads them as
 * `raw_image` beside the clip; those frames are what the model was shown. They
 * get a strip of their own with their times, and the gallery keeps the clip -
 * the same frames twice, once unlabelled in the gallery's strip, was the
 * alternative.
 *
 * Photographs only count as key frames when the record says they are - frame
 * times on the analysis, or a clip beside them. An area scan submitted as a
 * single photograph, before there were clips, is still that photograph in the
 * gallery.
 */
function scanMedia(
  assessment: AssessmentWithContext,
  attempt: number,
  entry: AttemptEntry | null | undefined
): { clip: ViewerItem[]; frames: KeyFrameItem[]; all: ViewerItem[] } {
  const items = viewerItems(assessment.media, attempt);
  const times = new Map(readKeyFrames(entry).map((frame) => [frame.seq, frame.t_ms]));
  const source = new Map(assessment.media.items.map((item) => [item.id, item]));
  const isFrame = (item: ViewerItem) => source.get(item.id)?.kind === "raw_image";

  const hasClip = items.some((item) => item.kind === "video");
  if (!hasClip && times.size === 0) return { clip: items, frames: [], all: items };

  const clip = items.filter((item) => !isFrame(item));
  const frames = items.filter(isFrame).map((item, index): KeyFrameItem => {
    const ms = times.get(source.get(item.id)!.seq);
    const at = ms === undefined ? null : sweepTime(ms);
    return {
      ...item,
      label: `Key frame ${index + 1}`,
      caption: at ? `${at} into the sweep` : undefined,
      at,
      ms: ms ?? null,
    };
  });
  return { clip, frames, all: [...clip, ...frames] };
}

function readKeyFrames(entry: AttemptEntry | null | undefined) {
  const parsed = keyFrameSchema.array().safeParse(entry?.input?.frames);
  return parsed.success ? parsed.data : [];
}

/** The clip's length, which the timeline spans. Null on a sweep that never said. */
function readDurationMs(entry: AttemptEntry | null | undefined): number | null {
  const seconds = entry?.input?.duration_seconds;
  return typeof seconds === "number" && seconds > 0 ? seconds * 1000 : null;
}

function readQuality(entry: AttemptEntry | null | undefined): CaptureQuality | null {
  const parsed = captureQualitySchema.safeParse(entry?.input?.quality);
  return parsed.success ? parsed.data : null;
}

/**
 * A moment in the sweep, as a stopwatch reads it: `00:05.60`.
 *
 * To the hundredth, because key frames are cut fractions of a second apart.
 * Whole seconds labelled a frame at 0.4s "0:00" and two at 5.1s and 5.6s both
 * "0:05", while the timeline drew each where it really was - two different
 * answers to "when". Clock-shaped rather than `5.6s` so it reads as a point in
 * the clip, not as a length of time.
 */
function sweepTime(ms: number): string {
  const centis = Math.round(ms / 10);
  const minutes = Math.floor(centis / 6000);
  const seconds = Math.floor(centis / 100) % 60;
  return `${pad2(minutes)}:${pad2(seconds)}.${pad2(centis % 100)}`;
}

/** Whole seconds, for the ruler's labels and the sweep's length: `00:04`. */
function sweepClock(ms: number): string {
  const seconds = Math.round(ms / 1000);
  return `${pad2(Math.floor(seconds / 60))}:${pad2(seconds % 60)}`;
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * The key frames a finding was seen in, the one that shows it best first.
 *
 * The analysis names them by `frame_seqs`: the `seq` of each `raw_image` on the
 * same attempt as the finding, so `attempt` is the enclosing entry's, not the
 * card's. The strip's own item is preferred, for its label and its time in the
 * clip; the bare media item stands in when the finding's attempt is not the one
 * the strip is drawing. A seq with no signed URL behind it is left out rather
 * than drawn as a broken tile.
 */
function framesFor(
  finding: Finding,
  media: AssessmentWithContext["media"],
  attempt: number | undefined,
  frames: KeyFrameItem[]
): ViewerItem[] {
  if (!finding.frame_seqs?.length || attempt === undefined) return [];
  return finding.frame_seqs.flatMap((seq): ViewerItem[] => {
    const item = media.items.find(
      (candidate) =>
        candidate.kind === "raw_image" && candidate.attempt === attempt && candidate.seq === seq
    );
    if (!item?.url) return [];
    return [
      frames.find((frame) => frame.id === item.id) ?? {
        id: item.id,
        url: item.url,
        kind: "image",
        label: `Key frame ${seq}`,
      },
    ];
  });
}

/** The phone's reasons, as a technician would say them. */
const QUALITY_WORDS: Record<CaptureQualityReason, string> = {
  too_short: "too short",
  too_dark: "too dark",
  too_bright: "too bright",
  too_shaky: "moved too fast",
  too_few_views: "too few different views",
  no_sweep: "pointed at one spot",
};

/**
 * Said only when the technician overrode the phone. A sweep that passed needs
 * no note, and one that failed and was re-shot is not the sweep on the card.
 * The phone always offers "use it anyway" - a dark basement fails every frame,
 * and the check never blocks work - so this is a fact about the evidence, not
 * a fault on the technician.
 */
function SweepQualityNote({ quality }: { quality: CaptureQuality | null }) {
  if (!quality?.overridden) return null;
  const reasons = quality.reasons.map((reason) => QUALITY_WORDS[reason]);
  return (
    <p className="scan-quality">
      <TriangleAlert aria-hidden />
      <span>
        {reasons.length > 0
          ? `The technician used a sweep the phone flagged: ${reasons.join(", ")}.`
          : "The technician used a sweep the phone flagged."}
      </span>
    </p>
  );
}

/**
 * The newest sweep has not been read: still being checked, or every try
 * failed. Worded so neither can be read as a clean sweep. The failure is the
 * same category sentence the phone shows, never the provider's message.
 */
function AnalysisUnavailable({ status }: { status: AnalysisStatus }) {
  const retry = status.retry ?? 0;
  return (
    <p className="scan-unavailable">
      <CircleSlash aria-hidden />
      <span>
        {status.state === "analysing" && retry > 0 ? (
          <>
            <strong>Taking longer than expected.</strong> Trying again ({retry} of{" "}
            {ANALYSIS_MAX_RETRIES}). Its findings appear here when it answers.
          </>
        ) : status.state === "analysing" ? (
          <>
            <strong>Being checked.</strong> This sweep is with the hazard analysis now, and its
            findings appear here when it answers.
          </>
        ) : (
          <>
            <strong>Not checked.</strong> {analysisFailureSentence(status.reason)} This sweep was
            not checked for hazards, which is not the same as none being found.
          </>
        )}
      </span>
    </p>
  );
}

/**
 * A hazard on the sweep: its number on the card, what it is, the tier it was
 * filed at, and the key frames it was seen in. `seenIn` is `framesFor` per
 * finding, in the same order, so the numbers are the finding tiles' numbers.
 */
type HazardSpan = {
  number: number;
  text: string;
  severity: SeverityTier;
  dismissed: boolean;
  frameIds: string[];
};

function hazardSpans(findings: Finding[], seenIn: ViewerItem[][]): HazardSpan[] {
  return findings.map((finding, index) => ({
    number: index + 1,
    text: finding.text,
    severity: effectiveSeverity(finding),
    dismissed: isDismissed(finding),
    frameIds: (seenIn[index] ?? []).map((frame) => frame.id),
  }));
}

/**
 * The frames the model was shown, as a carousel, over a timeline of the sweep.
 *
 * Pictures and their times only. Which hazards each frame shows is the
 * timeline's job: it said the same thing as the badges that used to sit under
 * every frame, better, and the two together were the same fact twice.
 *
 * One row that scrolls rather than wraps, because the order is the point: it
 * is the sweep, left to right. The arrows page it for a mouse with no
 * horizontal wheel, and only appear when there is somewhere to page to; the
 * fade on an edge says the same to anyone who does not look at them.
 *
 * **Selecting a hazard** - on the timeline - lights the frames it was seen in,
 * dims the rest and brings the first into view. It is one piece of state for
 * the strip and the timeline both, so the two can never disagree about what is
 * selected.
 */
function KeyFrameStrip({
  frames,
  hazards,
  durationMs,
  title,
}: {
  frames: KeyFrameItem[];
  hazards: HazardSpan[];
  durationMs: number | null;
  title: string;
}) {
  const [at, setAt] = React.useState<number | null>(null);
  const [selected, setSelected] = React.useState<number | null>(null);
  const [hovered, setHovered] = React.useState<string | null>(null);
  const rail = React.useRef<HTMLOListElement>(null);
  const [more, setMore] = React.useState({ before: false, after: false });

  const measure = React.useCallback(() => {
    const el = rail.current;
    if (!el) return;
    // `abs`: in a right-to-left document scrollLeft runs negative.
    const left = Math.abs(el.scrollLeft);
    setMore({ before: left > 1, after: left + el.clientWidth < el.scrollWidth - 1 });
  }, []);

  React.useEffect(() => {
    const el = rail.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, frames.length]);

  if (frames.length === 0) return null;

  const page = (direction: 1 | -1) => {
    const el = rail.current;
    el?.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: "smooth" });
  };

  const live = hazards.filter((hazard) => !hazard.dismissed);
  const marksOf = (id: string) => live.filter((hazard) => hazard.frameIds.includes(id));
  const flagged = frames.filter((frame) => marksOf(frame.id).length > 0).length;
  const focus = hazards.find((hazard) => hazard.number === selected) ?? null;
  const lit = new Set(focus?.frameIds ?? []);

  const select = (number: number | null) => {
    setSelected(number);
    const hazard = hazards.find((candidate) => candidate.number === number);
    const first = hazard && frames.find((frame) => hazard.frameIds.includes(frame.id));
    if (!first) return;
    // `nearest`, so a frame already in view does not jolt the page or the rail.
    rail.current
      ?.querySelector(`[data-frame="${first.id}"]`)
      ?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  };

  return (
    <div
      className={cn(
        "keyframes",
        focus && `keyframes--focus-${focus.dismissed ? "dismissed" : focus.severity}`
      )}
      onKeyDown={(event) => {
        if (event.key === "Escape" && selected !== null) {
          event.stopPropagation();
          setSelected(null);
        }
      }}
    >
      <div className="keyframes__head">
        <p className="keyframes__title">
          Key frames
          <span className="keyframes__summary tnum">
            {focus ? (
              <>
                Hazard {focus.number} appears in {lit.size} of {frames.length}{" "}
                {frames.length === 1 ? "frame" : "frames"}
                <button
                  type="button"
                  className="keyframes__clear"
                  onClick={() => setSelected(null)}
                >
                  Show all frames
                </button>
              </>
            ) : (
              <>
                {frames.length} {frames.length === 1 ? "frame" : "frames"}
                {durationMs !== null && ` across ${sweepClock(durationMs)}`}
                {flagged > 0 && `, ${flagged} with hazards`}
              </>
            )}
          </span>
        </p>
        {(more.before || more.after) && (
          <span className="keyframes__nav">
            <IconButton
              label="Earlier frames"
              size="sm"
              disabled={!more.before}
              onClick={() => page(-1)}
            >
              <ChevronLeft aria-hidden />
            </IconButton>
            <IconButton
              label="Later frames"
              size="sm"
              disabled={!more.after}
              onClick={() => page(1)}
            >
              <ChevronRight aria-hidden />
            </IconButton>
          </span>
        )}
      </div>

      <ol
        ref={rail}
        onScroll={measure}
        className={cn(
          "keyframes__rail",
          more.before && "keyframes__rail--more-before",
          more.after && "keyframes__rail--more-after"
        )}
      >
        {frames.map((frame, index) => {
          return (
            <li
              key={frame.id}
              data-frame={frame.id}
              className={cn(
                "frame-card",
                focus && (lit.has(frame.id) ? "frame-card--lit" : "frame-card--dim"),
                hovered === frame.id && "frame-card--hovered"
              )}
              onMouseEnter={() => setHovered(frame.id)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="frame-card__media">
                <MediaTile item={frame} thumb onOpen={() => setAt(index)} />
                {frame.at && <span className="frame-card__time tnum">{frame.at}</span>}
              </div>
            </li>
          );
        })}
      </ol>

      <SweepTimeline
        frames={frames}
        hazards={hazards}
        durationMs={durationMs}
        selected={selected}
        lit={lit}
        hovered={hovered}
        onSelect={(number) => select(number === selected ? null : number)}
        onHover={setHovered}
        onOpen={setAt}
      />

      {at !== null && (
        <MediaLightbox
          items={frames}
          title={title}
          index={at}
          onIndex={setAt}
          onClose={() => setAt(null)}
        />
      )}
    </div>
  );
}

/** Ruler steps, in seconds, and the minor tick between labels at each. */
const RULER_STEPS: [number, number | null][] = [
  [1, null],
  [2, 1],
  [5, 1],
  [10, 2],
  [15, 5],
  [30, 10],
  [60, 15],
  [120, 30],
];

/**
 * Labels at the first step that leaves six or fewer of them - a 0:11 sweep is
 * labelled every two seconds and ticked every one - so the ruler reads the same
 * whether the sweep ran ten seconds or a minute.
 */
function rulerMarks(totalMs: number): { ms: number; major: boolean }[] {
  const seconds = totalMs / 1000;
  const [step, minor] = RULER_STEPS.find(([candidate]) => seconds / candidate <= 6) ?? [300, 60];
  const unit = (minor ?? step) * 1000;
  const marks: { ms: number; major: boolean }[] = [];
  for (let ms = 0; ms <= totalMs; ms += unit) {
    marks.push({ ms, major: ms % (step * 1000) === 0 });
  }
  return marks;
}

/**
 * The sweep, start to end, as a review track: a ruler with a diamond for every
 * key frame, and one lane per hazard - its name on the left, the frames it was
 * seen in on the right.
 *
 * ── Read like a trace view ──────────────────────────────────────────────────
 *
 * Each lane carries its own label - tier icon, number, the hazard in words - so
 * the track reads without hovering anything, the way a span list in a tracing
 * tool does. Hazards overlap, two seen in the same frames being the common
 * case, and overlapping bars cannot say which covers which moment, so each gets
 * a lane; thin lanes are what keep it short.
 *
 * ── Dots are evidence, the line between them is not ────────────────────────
 *
 * A dot is a key frame the model saw the hazard in. The faint line joining a
 * hazard's dots says it was probably in view between them, which is an
 * inference: the model only ever saw the key frames. Drawing the sightings as
 * the strong mark keeps a reviewer from reading seven seconds of line as seven
 * seconds of footage somebody checked. A dismissed hazard is hollow dots on a
 * dashed line - on the record where the technician said it, not a live hazard.
 *
 * ── Scrubbing snaps to frames ───────────────────────────────────────────────
 *
 * Moving across the track snaps one line to the nearest key frame, says its
 * time on the ruler and outlines that frame in the strip above. It snaps
 * because between frames there is nothing to show. Pressing the ruler opens the
 * frame under the line; pressing a lane selects the hazard, which the strip
 * shows by lighting its frames. The diamonds are buttons too, for the keyboard.
 *
 * Nothing renders on a sweep with no frame times - one from before key frames
 * carried none - because a ruler with nothing on it is decoration.
 */
function SweepTimeline({
  frames,
  hazards,
  durationMs,
  selected,
  lit,
  hovered,
  onSelect,
  onHover,
  onOpen,
}: {
  frames: KeyFrameItem[];
  hazards: HazardSpan[];
  durationMs: number | null;
  selected: number | null;
  lit: Set<string>;
  hovered: string | null;
  onSelect: (number: number) => void;
  onHover: (id: string | null) => void;
  onOpen: (index: number) => void;
}) {
  const plot = React.useRef<HTMLDivElement>(null);

  const timed = frames.flatMap((frame, index) =>
    frame.ms === null ? [] : [{ id: frame.id, ms: frame.ms, index }]
  );
  if (timed.length === 0) return null;
  const times = new Map(timed.map((frame) => [frame.id, frame.ms]));

  // The clip's own length, or just past the last frame on a sweep that did not
  // say - never shorter than the frames on it.
  const total = Math.max(durationMs ?? 0, Math.max(...times.values()) + 500);
  const at = (ms: number) => ({ "--at": ms / total }) as React.CSSProperties;
  const current = hovered !== null ? (times.get(hovered) ?? null) : null;

  const lanes = hazards.flatMap((hazard) => {
    const sightings = hazard.frameIds
      .flatMap((id) => (times.has(id) ? [times.get(id)!] : []))
      .sort((a, b) => a - b);
    if (sightings.length === 0) return [];
    return [{ ...hazard, sightings, from: sightings[0]!, to: sightings[sightings.length - 1]! }];
  });

  const scrub = (event: React.PointerEvent) => {
    const box = plot.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    // Over the labels is reading a hazard's name, not scrubbing.
    if (event.clientX < box.left - 8) {
      if (hovered !== null) onHover(null);
      return;
    }
    const ms = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width)) * total;
    const nearest = timed.reduce((best, frame) =>
      Math.abs(frame.ms - ms) < Math.abs(best.ms - ms) ? frame : best
    );
    if (nearest.id !== hovered) onHover(nearest.id);
  };
  const openCurrent = () => {
    const frame = timed.find((candidate) => candidate.id === hovered);
    if (frame) onOpen(frame.index);
  };

  return (
    <div
      className="sweep-track"
      onPointerMove={(event) => event.pointerType === "mouse" && scrub(event)}
      onPointerLeave={() => onHover(null)}
    >
      <div className="sweep-track__row sweep-track__row--ruler">
        <span aria-hidden />
        <div ref={plot} className="sweep-ruler" onClick={openCurrent}>
          {rulerMarks(total).map((mark) => (
            <span
              key={mark.ms}
              aria-hidden
              className={cn("sweep-ruler__tick", mark.major && "sweep-ruler__tick--major")}
              style={at(mark.ms)}
            >
              {mark.major && <span className="sweep-ruler__label tnum">{sweepClock(mark.ms)}</span>}
            </span>
          ))}
          {current !== null && (
            <span className="sweep-ruler__readout tnum" style={at(current)} aria-hidden>
              {sweepTime(current)}
            </span>
          )}
          {timed.map((frame) => (
            <button
              key={frame.id}
              type="button"
              aria-label={`Open key frame ${frame.index + 1}, ${sweepTime(frame.ms)} into the sweep`}
              className={cn(
                "sweep-keyframe",
                lit.has(frame.id) && "sweep-keyframe--lit",
                hovered === frame.id && "sweep-keyframe--current"
              )}
              style={at(frame.ms)}
              onClick={(event) => {
                event.stopPropagation();
                onOpen(frame.index);
              }}
              onFocus={() => onHover(frame.id)}
              onBlur={() => onHover(null)}
            />
          ))}
        </div>
      </div>

      {lanes.map((hazard) => {
        const Icon = hazard.dismissed ? CircleSlash : SEVERITY_ICONS[hazard.severity];
        const count = hazard.sightings.length;
        const range =
          hazard.from === hazard.to
            ? sweepTime(hazard.from)
            : `${sweepTime(hazard.from)} to ${sweepTime(hazard.to)}`;
        return (
          <button
            key={hazard.number}
            type="button"
            aria-pressed={selected === hazard.number}
            aria-label={`Hazard ${hazard.number}: ${hazard.text}. ${
              hazard.dismissed ? "Dismissed" : SEVERITY_META[hazard.severity].label
            }, seen in ${count} ${count === 1 ? "frame" : "frames"}, ${range}.`}
            className={cn(
              "sweep-track__row",
              "sweep-lane",
              `sweep-lane--${hazard.dismissed ? "dismissed" : hazard.severity}`,
              selected === hazard.number && "sweep-lane--selected",
              selected !== null && selected !== hazard.number && "sweep-lane--muted"
            )}
            onClick={() => onSelect(hazard.number)}
          >
            <span className="sweep-lane__label" title={hazard.text} aria-hidden>
              <Icon className="sweep-lane__icon" />
              <span className="sweep-lane__number tnum">{hazard.number}</span>
              <span className="sweep-lane__text">{hazard.text}</span>
            </span>
            <span className="sweep-lane__plot" aria-hidden>
              <span
                className="sweep-lane__span"
                style={
                  {
                    "--start": hazard.from / total,
                    "--span": (hazard.to - hazard.from) / total,
                  } as React.CSSProperties
                }
              />
              {hazard.sightings.map((ms) => (
                <span
                  key={ms}
                  className={cn("sweep-lane__dot", ms === current && "sweep-lane__dot--current")}
                  style={at(ms)}
                />
              ))}
            </span>
          </button>
        );
      })}

      {current !== null && <span className="sweep-track__cursor" style={at(current)} aria-hidden />}
    </div>
  );
}

/**
 * The frame a finding is shown against when it names none of its own.
 *
 * A finding from a sweep with key frames carries the ones it was seen in - see
 * `framesFor` - and the best of them is what its tile and its dialog show. One from before
 * key frames has nothing to point at, and the sweep's own still stands in: it
 * is the recording the hazard was found in, which is the honest thing to show
 * beside it.
 *
 * The clip's poster where the scan is a clip, the photograph itself where it is
 * a photograph - `viewerItems` has already put the evidence first.
 */
function scanStill(media: ViewerItem[]): string | null {
  const first = media[0];
  if (!first) return null;
  return (first.kind === "video" ? first.poster : first.url) ?? null;
}

/**
 * One finding, in two lines - and everything about it one press away.
 *
 * A sweep raises two or three of these and the card holds two columns of them,
 * so every row this spends it spends six times. What it says at rest is the
 * two things a supervisor scans a record for: **what was found**, and **what
 * was done about it**. Both truncate at one line; neither is lost, because the
 * tile is a button onto the same finding in full.
 *
 * ── Why a dialog rather than a taller tile ─────────────────────────────────
 *
 * The rest of it - the detector's confidence, the technician's note, a
 * severity they overruled and why, the control in the words they typed - is
 * what somebody reads when a finding has already caught their eye. Rendering
 * all of it on every tile made the common case (scan six hazards, none of them
 * surprising) pay for the rare one, and a tile whose height depends on how much
 * the technician typed cannot be scanned as a column at all.
 *
 * ── The two things on the row, and where they sit ──────────────────────────
 *
 * The severity chip is pinned to the top right rather than led with: the reader
 * already knows every finding has a tier, and the sentence saying what was
 * found is the only line here that is news. The acknowledgement's timestamp is
 * pinned to the far right of its own line rather than running on after the
 * control - a column of times reads as a column; times that start wherever the
 * sentence above them ended read as noise.
 *
 * The detector's confidence is not on the tile. It is a fact about the model,
 * not about the job, and it was competing for the top line with the hazard.
 * It is the first thing in the dialog.
 */
function FindingRow({
  finding,
  index,
  media,
  frames,
  timezone,
}: {
  finding: Finding;
  index: number;
  /**
   * The attempt's evidence, for the dialog: the frame it shows beside the
   * finding, and the set the reader lands in when they press it. See
   * `scanStill`.
   */
  media: ViewerItem[];
  /** The key frames this finding was seen in, best first, for the dialog. See `framesFor`. */
  frames: ViewerItem[];
  timezone: string;
}) {
  const [open, setOpen] = React.useState(false);
  const answered = isAnswered(finding);
  const said = answered
    ? answerText(finding)
    : finding.suggested_control
      ? `Suggested: ${finding.suggested_control}`
      : "Nobody has said anything about this.";

  return (
    <li className="scan-finding-item">
      {/* A real button, not a click handler on the `li`: this is the only way
          into the detail, so it has to be reachable by keyboard and announced
          as opening a dialog. Everything inside is phrasing content for the
          same reason - a `<p>` in a button is invalid, and the truncation
          works the same on a span. */}
      <button
        type="button"
        className="scan-finding"
        aria-haspopup="dialog"
        title="Show this finding in full"
        onClick={() => setOpen(true)}
      >
        {/* No picture on the tile. The card already carries the clip and the
            key frames, and a thumbnail on every hazard as well made it a wall
            of near-identical images; the frames a hazard was seen in are the
            first thing its dialog shows. */}
        <span className="scan-finding__body">
          <span className="scan-finding__text">
            {/* The reference a reviewer uses out loud - "the second one". */}
            <span className="scan-finding__index tnum">{index + 1}</span>
            <span className="scan-finding__label">{finding.text}</span>

            {/* A mark, not a pill: this line has no room for a word, and it is
              the one thing about the finding nothing else on the tile says -
              that a person filed it BELOW the analysis. It is spelled out in
              full in the dialog. A stop-work moves the filed severity too, and
              gets no mark: the line below opens with "Work stopped" in bold and
              carries the octagon beside it, so a second mark up here would be
              the same fact twice on a two-line tile. `isSeverityOverridden` is
              one-directional for exactly that reason. */}
            {isSeverityOverridden(finding) && (
              <span className="scan-finding__mark" title={overrideNote(finding)}>
                <TriangleAlert aria-hidden />
                <span className="sr-only">Severity set by the technician</span>
              </span>
            )}

            {isDismissed(finding) ? (
              <DismissedChip />
            ) : (
              <SeverityChip severity={effectiveSeverity(finding)} size="sm" />
            )}

            {/* The tile is a button and nothing on it said so - a hover tint is
              not an affordance on a screen nobody has hovered yet. A glyph
              rather than a "View" link: the whole tile is the target, and a
              second word competing with the hazard's own sentence is what this
              layout spent two rows getting rid of.

              A chevron, and not an expand mark or an ellipsis. Expand arrows
              promise the *frame* gets bigger, which is what every gallery on
              the machine uses them for; an ellipsis promises a menu of choices.
              What opens is one hazard's own record, and a chevron is the mark
              this product uses for that - the same one the phone's cards and
              checklist rows carry, so a supervisor moving between the two
              clients is following one affordance.

              It is drawn as its own square button rather than a bare glyph,
              but it is not one: a `<button>` inside a `<button>` is invalid,
              and there is nothing for a second control to do that pressing the
              tile does not already do. */}
            <span className="scan-finding__expand" aria-hidden>
              <ChevronRight />
            </span>
          </span>

          {/* Two lines, not one run-on sentence. What the technician decided and
            when they decided it are the two facts a reviewer matches against
            the rest of the visit's timeline, so they share the top line with
            the time pinned right; what they actually did is a sentence, and it
            gets a line of its own under them. */}
          <span className={answerClass(finding)}>
            <span className="scan-finding__verdict">
              {/* A green tick beside "Work stopped" reads as "all fine here",
                which is the opposite of what the record says. */}
              {!answered ? (
                <CircleX aria-hidden />
              ) : isStopWork(finding) ? (
                <OctagonX aria-hidden />
              ) : (
                <CircleCheck aria-hidden />
              )}
              <strong>{dispositionCopy(finding.disposition, finding.severity).label}</strong>
              {answered && finding.acknowledged_at && (
                <span className="scan-finding__ack tnum">
                  {formatTimeIn(finding.acknowledged_at, timezone)}
                </span>
              )}
            </span>
            {said !== null && <span className="scan-finding__said">{said}</span>}
          </span>
        </span>
      </button>

      {open && (
        <FindingModal
          finding={finding}
          media={media}
          frames={frames}
          timezone={timezone}
          onClose={() => setOpen(false)}
        />
      )}
    </li>
  );
}

/**
 * Which of the three the answer line is: unanswered, answered, or answered by
 * refusing to work. The third is an escalation rather than a severity, so it
 * gets its own tone rather than being inferred from the tier above it.
 */
function answerClass(finding: Finding): string {
  if (!isAnswered(finding)) return "scan-finding__open";
  return cn("scan-finding__control", isStopWork(finding) && "scan-finding__control--stop");
}

/**
 * What the technician said, in their words where they gave any. Null for a
 * stop-work, always: the sheet never asks why work was stopped - the hazard is
 * the why - so "No reason was recorded" read as a gap in a record that has
 * none, and a reason that is there is an earlier answer's, left behind when the
 * answer changed. Their note, if any, still shows under its own label.
 */
function answerText(finding: Finding): string | null {
  if (finding.disposition === "controlled") return finding.control ?? "No control was recorded.";
  if (finding.disposition === "stop_work") return null;
  return finding.disposition_reason ?? "No reason was recorded.";
}

/**
 * Worst first by the severity each is FILED at, so a hazard the technician
 * refused to work under leads even where the analysis was relaxed about it.
 * Dismissed ones go last: the person on site said there is no hazard there,
 * and a "Not a hazard" at the head of the list over a real Stop reads as the
 * worst thing on the card.
 */
function compareFindings(a: Finding, b: Finding): number {
  return (
    Number(isDismissed(a)) - Number(isDismissed(b)) ||
    compareSeverityDesc(effectiveSeverity(a), effectiveSeverity(b))
  );
}

/**
 * In place of the severity chip on a finding the technician dismissed. The
 * analysis's tier would still read as a live Stop on the tile; it stays on the
 * finding's own record, under Analysis, where it is the model's claim.
 */
function DismissedChip() {
  return (
    <Badge tone="neutral" size="sm" icon={<CircleSlash />}>
      Dismissed
    </Badge>
  );
}

/** Both severities, because the marker is useless without the one it disagrees with. */
function overrideNote(finding: Finding): string {
  return `Filed as ${SEVERITY_META[effectiveSeverity(finding)].label} by the technician. The analysis found ${SEVERITY_META[finding.severity].label}.`;
}

/**
 * One finding, in full.
 *
 * ── The heading is the hazard, on one line ────────────────────────────────
 *
 * It used to be "Finding 2", with the sentence set again underneath it - a
 * heading that named a position in a list nobody could see any more, over the
 * one line that says what this dialog is about. The index is how a reviewer
 * points at a row on the *card*; once they are in the record it is noise. The
 * heading truncates to one line, and the first field carries the sentence in
 * full.
 *
 * No severity chip beside it. The tier the analysis gave is a badge on the
 * Finding field, and a tier the technician changed is said in the Response, so
 * a third chip in the header was the same fact again.
 *
 * ── The frame, on the left, and it opens ───────────────────────────────────
 *
 * A hazard is a thing somebody photographed, and reading "exposed conductor at
 * the pedestal" without the picture is reading a transcript of the evidence.
 * It is the key frame that shows the finding best where the finding names any -
 * `framesFor` - and the sweep's own still otherwise (`scanStill`). The other
 * frames it was seen in sit under it, small, each opening the lightbox at that
 * frame: one angle on a hazard is the model's preference, several are the
 * evidence. It holds its
 * column at a fixed 3:4 either way - the phone records sweeps upright - so the
 * facts beside it start on the same line whatever shape the frame turned out
 * to be.
 *
 * Pressing it opens the lightbox on the attempt's own set, at that frame. A
 * 176px still is for recognising the scene, never for reading a hazard at the
 * edge of it, and a picture that does not open when a reader presses it is the
 * one affordance every gallery on the machine has already taught them to
 * expect.
 *
 * ── Two groups of fields ───────────────────────────────────────────────────
 *
 * Six fields of equal weight in one grid gave a reader nothing to tell them
 * where to start. They fall into the two claims the record makes, in the order
 * they happened: the **Analysis** - the finding in full, the tier the model
 * gave it, how sure it was, what it proposed - and then the **Response** - what
 * the person standing there decided, when, and in their words. A tier the
 * technician changed is part of their answer.
 *
 * Sentences are boxed fields - the finding, and what the technician wrote - so
 * it is clear which words came from where. The short facts are not boxed: a
 * box apiece made the dialog taller than the frame beside it for four words.
 * The model's tier and confidence are badges on the finding's own card; the
 * outcome and its time share a line; a tier the technician changed is said in
 * the Response, because changing it was something they did. The label over
 * the technician's sentence changes with the outcome - "What was done" for a
 * control, "Why" for the three answers that disagree with the analysis.
 *
 * **There is no separate stop-work chip.** `Outcome` renders the disposition's
 * own label, and for `stop_work` that label *is* "Work stopped", with the
 * octagon beside it. A chip above saying the same thing was the same fact
 * twice. The override marker stays, because nothing else in the dialog says a
 * person overruled the analysis.
 *
 * ── The suggestion is a card, and it says who wrote it ─────────────────────
 *
 * A `<dt>` reading "Suggested control" put the model's words in the same
 * typographic voice as the technician's, where the one thing that matters about
 * them - *nobody did this, a model proposed it* - was carried by a single
 * adjective. Decisions #40 turns on that distinction. So it closes the Analysis
 * group as a tinted card with the mark on its heading, before the Response
 * group that records what happened.
 *
 * Still no model or build number. That is a fact for whoever is auditing the
 * detector, not for whoever is auditing the job - it is on the finding, in the
 * API's response and in the export, which is where an audit reads it from.
 */
function FindingModal({
  finding,
  media,
  frames,
  timezone,
  onClose,
}: {
  finding: Finding;
  media: ViewerItem[];
  frames: ViewerItem[];
  timezone: string;
  onClose: () => void;
}) {
  const [lightbox, setLightbox] = React.useState<number | null>(null);
  const answered = isAnswered(finding);
  const said = answered ? answerText(finding) : null;
  const [best, ...others] = frames;
  // The finding's own frames, and only those: the clip and the key frames that
  // show something else are the card's evidence, not this hazard's. The
  // sweep's own set stands in only when the finding names no frame at all.
  const evidence = frames.length > 0 ? frames : media;
  const openAt = (item: ViewerItem | undefined) =>
    setLightbox(
      item
        ? Math.max(
            0,
            evidence.findIndex((candidate) => candidate.id === item.id)
          )
        : 0
    );

  return (
    <Modal
      open
      onClose={onClose}
      title={
        // One line, whatever the hazard's length: a wrapped heading pushes the
        // dialog's body down with it. The sentence in full is the first field
        // of the Analysis below, and the tier is the badge on that field.
        <span className="finding-modal__title-text" title={finding.text}>
          {finding.text}
        </span>
      }
      className="finding-modal"
    >
      <div className="finding-detail">
        <FindingFrame
          src={best?.url ?? scanStill(media)}
          onOpen={evidence.length > 0 ? () => openAt(best) : undefined}
          others={others}
          onOpenOther={openAt}
        />

        <div className="finding-detail__body">
          {/* What the model saw, rated and proposed - first, because it is
              what the technician was answering. Free text in a box of its own;
              the short facts on one line under it, because a box apiece made a
              word like "High" cost as much height as a sentence. */}
          <section className="finding-section">
            <h3 className="finding-section__label">Analysis</h3>
            {/* The finding and the model's two readings of it, as one card: the
                tier it gave and how sure it was are facts about this sentence,
                not about the job. Both are badges with a tooltip naming them,
                because a bare "High" says nothing about what is high. */}
            <div className="finding-fact">
              <span className="finding-fact__head">
                <span className="finding-fact__label">Finding</span>
                <span className="finding-fact__badges">
                  <span title={`Rated ${SEVERITY_META[finding.severity].label} by the analysis`}>
                    <SeverityChip severity={finding.severity} size="sm" />
                  </span>
                  <span
                    title={`Detector confidence: ${confidenceLabel(finding.confidence) ?? "not read"}`}
                  >
                    <Badge tone="neutral" size="sm" icon={<Gauge />}>
                      <span className="sr-only">Detector confidence: </span>
                      {confidenceLabel(finding.confidence) ?? "Not read"}
                    </Badge>
                  </span>
                </span>
              </span>
              <p className="finding-fact__value">{finding.text}</p>
            </div>

            {/* Offered, and never presented as something that happened. That
                the two are different fields is the whole reason a record can be
                trusted to say what the technician actually did - decisions #40 -
                and the mark on the heading carries that to a reader skimming. */}
            {finding.suggested_control && (
              <div className="aicard">
                <p className="aicard__head">
                  <span className="aicard__mark" aria-hidden>
                    <Sparkles />
                  </span>
                  <span>AI Suggested Control</span>
                </p>
                <p className="aicard__text">{finding.suggested_control}</p>
              </div>
            )}
          </section>

          {/* What the person standing there did about it: the answer and its
              time on one line, their own words boxed under it. */}
          <section className="finding-section">
            <h3 className="finding-section__label">Response</h3>
            <div className="finding-answer">
              <span className={outcomeClass(finding)}>
                {!answered ? (
                  <CircleX aria-hidden />
                ) : isStopWork(finding) ? (
                  <OctagonX aria-hidden />
                ) : (
                  <CircleCheck aria-hidden />
                )}
                {dispositionCopy(finding.disposition, finding.severity).label}
              </span>
              {/* Never blank. "Nobody has answered this yet" is a state of the
                  record, and an empty value is indistinguishable from a date
                  that failed to render. */}
              <span className="finding-answer__time tnum">
                {finding.acknowledged_at
                  ? `Answered ${formatDayTimeIn(finding.acknowledged_at, timezone)}`
                  : "Not answered yet"}
              </span>
            </div>

            {/* A tier the technician changed is part of their answer, so it is
                said here rather than on the analysis's card. Both tiers, because
                the change is useless without the one it disagreed with. */}
            {isSeverityOverridden(finding) && (
              <p className="finding-answer__override">
                <PenLine aria-hidden />
                <span>Filed as</span>
                <SeverityChip severity={effectiveSeverity(finding)} size="sm" />
                <span>instead of</span>
                <SeverityChip severity={finding.severity} size="sm" />
              </p>
            )}

            {/* The label follows the answer. A control and a rebuttal are
                different claims about the job, and one heading over both made
                the record say a hazard was dealt with when what the technician
                wrote was why it is not one. */}
            {said && (
              <div className="finding-fact">
                <span className="finding-fact__label">
                  {finding.disposition === "controlled" ? "What was done" : "Why"}
                </span>
                <p className="finding-fact__value">{said}</p>
              </div>
            )}
            {finding.note && (
              <div className="finding-fact">
                <span className="finding-fact__label">Note</span>
                <p className="finding-fact__value">{finding.note}</p>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Over the finding, not instead of it. `Modal` counts its scroll locks
          for exactly this, and closing the lightbox puts the reader back on the
          record they opened it from rather than on the card. */}
      {lightbox !== null && (
        <MediaLightbox
          items={evidence}
          title={frames.length > 0 ? "Frames showing this hazard" : "The recorded sweep"}
          index={lightbox}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </Modal>
  );
}

/**
 * The frame the hazard was found in, at one fixed shape, and the way into the
 * rest of the evidence.
 *
 * 4:3 whatever is behind it, and it holds that box when the still is missing or
 * fails to load: a column that collapses to nothing reflows the facts beside it
 * into a full-width block, and the dialog then has two layouts depending on
 * whether a 40kB thumbnail arrived.
 *
 * A button only when there is something to open. A frame that has nothing
 * behind it - no media on the attempt at all - must not be focusable and must
 * not take a cursor, because there is no dialog for it to lead to.
 */
function FindingFrame({
  src,
  onOpen,
  others = [],
  onOpenOther,
}: {
  src: string | null;
  onOpen?: () => void;
  /** The other key frames the hazard is seen in. */
  others?: ViewerItem[];
  onOpenOther?: (item: ViewerItem) => void;
}) {
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => setFailed(false), [src]);

  // One box either way, and the tag is the only thing that changes. Rendering
  // the image as a direct child of the figure when there is nothing to open
  // would put the 4:3 frame and the absolutely-positioned image on the same
  // element, which is two contradictory sets of rules for one box.
  const frame =
    src && !failed ? (
      <img src={src} alt="" className="finding-frame__image" onError={() => setFailed(true)} />
    ) : (
      <span className="finding-frame__empty">
        <ImageOff aria-hidden />
      </span>
    );

  return (
    <figure className="finding-frame">
      {onOpen ? (
        <button
          type="button"
          className="finding-frame__box finding-frame__box--open"
          onClick={onOpen}
          aria-haspopup="dialog"
          aria-label="Show the evidence for this finding, full size"
        >
          {frame}
          <span className="finding-frame__glyph">
            <Expand aria-hidden />
          </span>
        </button>
      ) : (
        <div className="finding-frame__box">{frame}</div>
      )}

      {others.length > 0 && (
        <div className="finding-frame__others">
          {others.map((item) => {
            const name = item.caption ? `${item.label}, ${item.caption}` : item.label;
            return (
              <button
                key={item.id}
                type="button"
                className="finding-frame__other"
                title={name}
                aria-haspopup="dialog"
                aria-label={`${name}. Also shows this hazard.`}
                onClick={() => onOpenOther?.(item)}
              >
                <img src={item.url} alt="" loading="lazy" decoding="async" />
              </button>
            );
          })}
        </div>
      )}
    </figure>
  );
}

/** The outcome's tone: open, answered, or answered by refusing to work. */
function outcomeClass(finding: Finding): string {
  if (!isAnswered(finding)) return "finding-fact__outcome finding-fact__outcome--open";
  return cn("finding-fact__outcome", isStopWork(finding) && "finding-fact__outcome--stop");
}

/**
 * What the tick on one checklist row actually rests on: a detector, a person,
 * or the two of them disagreeing.
 *
 * ── Why this stopped being a percentage ────────────────────────────────────
 *
 * It used to be "94%" behind a dial. That figure answers a question nobody
 * reviewing a job is asking. A supervisor looking at a set-up check wants to
 * know **who said this** - and in particular whether the machine and the person
 * holding the phone agreed, because the whole design of these checks is that the
 * technician may overrule the detector and the record has to show it when they
 * did. "94%" cannot say any of that, and on a row where the technician had
 * corrected the model it was printing the model's confidence in an answer that
 * is no longer on the record.
 *
 * So the column carries the *state* and the mark in front of it carries the
 * source. The figure is not gone - it is in the row's own dialog, under a
 * heading, where a number belongs - and the whole sentence rides on the row's
 * `title` and goes to a screen reader.
 *
 * The states, in the order they outrank each other:
 *
 * - **they disagree.** The detector read the row and the technician answered
 *   against that reading. The most important thing this row can say, so it
 *   outranks everything below - including the figure, which the phone nulls
 *   anyway, because a confidence no longer describes what ended up on the
 *   record.
 * - **no photograph covered it, and the technician confirmed it anyway.** Their
 *   own word, and the record says so.
 * - **no photograph covered it and they did not confirm it.** Not a detection of
 *   absence: "we did not look" and "it was not there" are different claims.
 * - **they agree**, and the detector's own answer is the row's answer.
 * - **nothing read it at all.** `detected` is absent on an attested row the
 *   detector is never asked about - the ladder's `ground_firm` - and on records
 *   written before the detector had a third state; the tick is a person's either
 *   way. Never "not in shot" here: that claims a photograph was taken and did
 *   not cover it, which only `not_assessed` says.
 *
 * There is no path that returns nothing. A blank in this column is
 * indistinguishable from a value that failed to render.
 */
function checkBasis(answer?: {
  present: boolean;
  detected?: DetectionState;
  confidence?: number | null;
}): RowBasis | null {
  // No answer at all is a row the catalogue lists and the submission never
  // mentioned, which is a different thing from any of the states below.
  if (!answer) return null;

  const sure = confidenceNote(answer.confidence);

  if (answer.detected === undefined) {
    return answer.present
      ? {
          text: "By technician",
          source: "hand",
          detail: "Nothing read this row. The technician confirmed it themselves.",
        }
      : {
          text: "Not confirmed",
          source: "none",
          detail: "Nothing read this row, and the technician did not confirm it.",
        };
  }

  if (answer.detected === "not_assessed") {
    return answer.present
      ? {
          text: "By technician",
          source: "hand",
          detail: "No photograph covered this. The technician confirmed it themselves.",
        }
      : {
          text: "Not in shot",
          source: "none",
          detail: "No photograph covered this, and the technician did not confirm it.",
        };
  }

  const found = answer.detected === "present";

  if (answer.present !== found) {
    return {
      text: "Corrected",
      source: "conflict",
      detail: found
        ? `The detector found this; the technician says it is not there.${sure}`
        : `The detector did not find this; the technician confirmed it is there.${sure}`,
    };
  }

  return found
    ? {
        text: "Found",
        source: "ai",
        detail: `The detector found this and the technician agreed.${sure}`,
      }
    : {
        text: "Not there",
        source: "ai",
        detail: `The detector could see where this would be and it was not there. The technician agreed.${sure}`,
      };
}

/**
 * How sure, as the clause it belongs in. Empty where nothing read the row - a
 * sentence explaining that there is no figure tells a reader nothing the state
 * in front of them ("Not in shot") has not already said.
 */
function confidenceNote(confidence?: number | null): string {
  const label = confidenceLabel(confidence);
  return label === null ? "" : ` Confidence was ${label.toLowerCase()}.`;
}

/**
 * Where a row's answer came from: the words, and the mark in front of them.
 *
 * `source` is the whole of what says a machine answered this - a spark, the same
 * mark the suggested control and the angle advisory wear, because "a model wrote
 * this" has to look the same everywhere on the page or it is not a mark, it is
 * decoration. `conflict` is the one state that gets a mark of its own: a
 * technician overruling the detector is the record's most load-bearing sentence
 * and it may not be the same quiet italic as "not in shot".
 *
 * `detail` is the sentence. It is never the only carrier of anything - the words
 * in `text` stand on their own - but it is where the figure went, and it is what
 * a screen reader reads instead of a two-word fragment.
 */
export type RowBasis = {
  text: string;
  source: "ai" | "hand" | "conflict" | "none";
  detail: string;
};

/** One catalogue row joined to whatever the submission said about it. */
type CheckRow = {
  code: string;
  label: string;
  answer?: { present: boolean; detected?: DetectionState; confidence?: number | null };
  /**
   * What the model wrote about this row, where a model read it.
   *
   * Kept apart from `answer` because they are different claims: `answer` is what
   * ended up on the record, and this is what was said about it. A technician who
   * corrected a detection has an `answer` that disagrees with its own `detail`,
   * and that disagreement is the most interesting thing either of them says.
   */
  detail?: ChecklistDetectionAnswer;
};

/**
 * The technician's answers off `metadata.items` / `.checks`, or none.
 *
 * A bad list is dropped rather than thrown on, and it is read on its own rather
 * than as part of the module's whole metadata object - the difference between a
 * card missing its checklist and a card that renders nothing at all. Same reader
 * the phone uses, including the legacy `{ code: boolean }` shape it widens.
 */
function readAnswers(raw: unknown): ChecklistAnswer[] {
  const parsed = checklistAnswers.safeParse(raw);
  return parsed.success ? parsed.data : [];
}

function checkRows(
  catalogue: { code: string; label: string }[],
  answers: {
    code: string;
    present: boolean;
    detected?: DetectionState;
    confidence?: number | null;
  }[],
  /**
   * The submission's detections, for the model's own words. The answers above
   * carry the tick and the reading; `reason` and `suggested_control` live on the
   * detection and reach a reader through the row's own dialog.
   */
  details: ChecklistDetectionAnswer[] = []
): CheckRow[] {
  const byCode = new Map(answers.map((answer) => [answer.code, answer]));
  const detailByCode = new Map(details.map((detail) => [detail.code, detail]));
  return catalogue.map((entry) => ({
    ...entry,
    answer: byCode.get(entry.code),
    detail: detailByCode.get(entry.code),
  }));
}

/**
 * A checklist the technician owns and a detector pre-filled - PPE's equipment
 * and the ladder's set-up checks, which are the same list rendered the same way
 * because they are now the same kind of fact.
 *
 * Three states, not two, and the third has its own glyph: an item no photograph
 * covered is not an item found missing. Colour is never the only carrier -
 * every row writes out where its answer came from beside it.
 */
function CheckList({
  rows,
  split,
  ticked = true,
  basisOf = (row) => checkBasis(row.answer),
}: {
  rows: CheckRow[];
  split?: boolean;
  /**
   * Whether these rows carry the technician's own answer. The card's do; a
   * superseded submission's do not - nobody was asked about photographs that
   * were replaced before the check was signed off - so its dialog leaves the
   * claim out rather than printing the detector's reading under a heading that
   * says a person made it.
   */
  ticked?: boolean;
  /**
   * Where each row's answer came from, in words. The default reads the
   * technician's tick against the detector's reading; the version history
   * passes one that only ever speaks for the detector, because a superseded
   * submission has no ticks on it - see `detectorBasis`.
   */
  basisOf?: (row: CheckRow) => RowBasis | null;
}) {
  const [open, setOpen] = React.useState<string | null>(null);
  const opened = rows.find((row) => row.code === open);

  return (
    <>
      <ul className={cn("checkitems", split && "checkitems--split")}>
        {rows.map((row) => {
          const notSeen = row.answer?.detected === "not_assessed" && !row.answer?.present;
          const basis = basisOf(row);
          return (
            <li
              key={row.code}
              className={cn(
                "checkitem",
                notSeen
                  ? "checkitem--unseen"
                  : row.answer?.present
                    ? "checkitem--ok"
                    : "checkitem--off"
              )}
            >
              {notSeen ? (
                <CircleHelp aria-hidden />
              ) : row.answer?.present ? (
                <CircleCheck aria-hidden />
              ) : (
                <CircleX aria-hidden />
              )}
              <span className="checkitem__label">{row.label}</span>
              {basis && (
                <span
                  className={cn("checkitem__meta", `checkitem__meta--${basis.source}`)}
                  title={basis.detail}
                >
                  {/* The spark means one thing on this page and it means it
                    everywhere: a model wrote this. The alert means the two
                    answers disagree. Everything else is a person's own word, in
                    the italic this card has always set one in. */}
                  {basis.source === "ai" && <Sparkles aria-hidden />}
                  {basis.source === "conflict" && <TriangleAlert aria-hidden />}
                  {basis.text}
                  {/* Two words at the end of a row are a fragment read aloud.
                    The sentence rides on the `title` for a cursor and here for a
                    screen reader, and it is the same sentence. */}
                  <span className="sr-only">. {basis.detail}</span>
                </span>
              )}

              {/* The same control the area scan's hazards carry, for the same
                reason: what the model actually said about this row is two or
                three sentences, and a checklist that prints them is no longer a
                checklist. A hover tooltip was the first idea and is the wrong
                one - it is unreachable on a touch screen, it cannot be copied,
                and it puts the model's advice somewhere a reviewer has to
                discover by accident. The phone keeps the note inline instead:
                there is no hover there and the person holding it is the one who
                has to act on the advice. */}
              {hasDetail(row) && (
                <button
                  type="button"
                  className="checkitem__more"
                  aria-haspopup="dialog"
                  title="What the detector said about this"
                  aria-label={`What the detector said about ${row.label}`}
                  onClick={() => setOpen(row.code)}
                >
                  {/* An `i`, not an ellipsis: an ellipsis promises a menu of
                      things to do, and what is behind this is a sentence to
                      read - what the detector made of the row, how sure it was,
                      what it suggests. The hazard tiles beside it carry a
                      chevron instead, because those lead to a record rather
                      than to an explanation of one line. */}
                  <Info aria-hidden />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {/* Outside the list, not inside it: a `ul` may only hold list items, and
          the dialog is in the top layer either way. */}
      {opened && <CheckModal row={opened} ticked={ticked} onClose={() => setOpen(null)} />}
    </>
  );
}

/**
 * Whether anything read this row, which is what the dialog is about.
 *
 * Not "did the model write prose": the figure moved off the row and into the
 * dialog, so a detection with a confidence and no words still has something a
 * reviewer came for. A row nothing looked at - an attested code, a record from
 * before the detector existed - has nothing behind it that the row does not
 * already say, and gets no control rather than an empty dialog.
 */
function hasDetail(row: CheckRow): boolean {
  return Boolean(row.detail) || row.answer?.detected !== undefined;
}

/** What the detector concluded, as a sentence rather than an enum. */
const DETECTED_WORDS: Record<DetectionState, string> = {
  present: "Seen in the photographs",
  absent: "Not there in the photographs",
  not_assessed: "Not visible in any photograph",
};

/**
 * One checklist row, in full: what the model read, how sure it was, what it
 * suggests, and what ended up on the record.
 *
 * The area scan's `FindingModal` in miniature, and deliberately the same
 * furniture - the bordered fields, the accent block under them - because they
 * are the same kind of record. What is missing is the frame: a detection is a
 * claim about a *set* of photographs rather than one crop of one of them, and a
 * thumbnail beside it would caption a row about boots with a picture of a hard
 * hat. The strip on the card behind this dialog is the evidence, whole.
 */
function CheckModal({
  row,
  ticked,
  onClose,
}: {
  row: CheckRow;
  ticked: boolean;
  onClose: () => void;
}) {
  const detail = row.detail;
  const reason = detail?.reason?.trim();
  const control = detail?.suggested_control?.trim();
  // The detection row where there is one, and the answer's own copy of the same
  // two fields where there is not - a record written before the detections
  // became rows carries them there and nowhere else.
  const detected = detail?.detected ?? row.answer?.detected ?? "not_assessed";
  const confidence = detail?.confidence ?? row.answer?.confidence ?? null;

  return (
    <Modal open onClose={onClose} title={row.label} className="finding-modal">
      <dl className="finding-detail__facts">
        <div className="finding-fact">
          <dt>What the detector read</dt>
          <dd>{DETECTED_WORDS[detected]}</dd>
        </div>

        {/* Absent rather than explained. `null` is what the schema stores for
            a look nobody took, and the row this dialog belongs to already says
            so in the fact directly above - "Not in shot", in words. A second
            row underneath it reading "None - nothing was read" was the same
            fact a second time, phrased as an apology for a missing number. */}
        {confidenceLabel(confidence) !== null && (
          <div className="finding-fact">
            <dt>Confidence</dt>
            <dd>{confidenceLabel(confidence)}</dd>
          </div>
        )}

        {/* Only where there is a tick to report. A superseded submission has
            none - see `ticked` - and printing the detector's own reading here
            would put the machine's answer under the technician's name. */}
        {ticked && row.answer && (
          <div className="finding-fact">
            <dt>On the record</dt>
            <dd className={outcomeToneClass(row.answer.present)}>
              {row.answer.present ? <CircleCheck aria-hidden /> : <CircleX aria-hidden />}
              {row.answer.present ? "Confirmed by the technician" : "Not confirmed"}
            </dd>
          </div>
        )}

        {reason && (
          <div className="finding-fact finding-fact--wide">
            <dt>What the detector saw</dt>
            <dd>{reason}</dd>
          </div>
        )}
      </dl>

      {/* Offered, and never presented as something that happened - the same
          distinction the area scan's dialog draws, drawn the same way. */}
      {control && (
        <section className="aicard">
          <p className="aicard__head">
            <span className="aicard__mark" aria-hidden>
              <Sparkles />
            </span>
            <span>AI Suggested Control</span>
          </p>
          <p className="aicard__text">{control}</p>
        </section>
      )}
    </Modal>
  );
}

/** The tick's tone in the dialog: the same two marks the row itself carries. */
function outcomeToneClass(present: boolean): string {
  return cn("finding-fact__outcome", !present && "finding-fact__outcome--open");
}

/* ───────────────────────────────── heights ─────────────────────────────── */

/**
 * The instrument and the photographs on the left, what was checked on the right.
 *
 * The photographs are a thumbnail strip under the dial rather than a lead tile
 * over it - see the `compact` note on `MediaGallery`. What this card answers is
 * an angle, and a 4:3 photograph stacked on the instrument that reads it made
 * the evidence and the reading look like two findings of equal weight.
 *
 * The version this replaces put the ladder photograph in the left half and
 * stacked the gauge on top of the checklist in the right - which gave a single
 * number a third of the card, ran the checklist down a column narrow enough to
 * wrap every label, and left the two half-width cards of a row at wildly
 * different heights. The reading and the checklist are the record; the
 * photographs are what half of it was read from, and they sit under the
 * instrument as tiles the same size as every other tile on the page.
 *
 * ── Two halves, two provenances ────────────────────────────────────────────
 *
 * The angle is a measurement and carries no confidence, because there is no
 * inference in it. The set-up checks beside it went through the same detector
 * PPE's items do and carry `detected` and `confidence` per row - so this card
 * renders them with the same component, and every row says whether a machine or
 * a person answered it. `ground_firm` is always a person: nothing optical
 * measures soil bearing capacity, and the detector is never asked.
 *
 * Both halves are per submission. The angle used to be taken once for the whole
 * check, which paired a re-shot set of photographs with the reading from before
 * the ladder was moved - see `heightsReading` in `@taskiq/shared`.
 */
/**
 * The instrument, its reading, and how far off target it is.
 *
 * Its own component because the version history draws the same thing: every
 * submission of this check carries the angle it was measured at, and a
 * reviewer comparing two attempts is comparing two of these.
 */
function AngleReading({
  angle,
  target,
  tolerance,
  advisory,
}: {
  angle: number;
  target: number;
  tolerance: number;
  /**
   * The model's one sentence about this reading - walk the feet in, and so on.
   *
   * **Advice, not a measurement**, and where it is drawn has to keep that
   * plain: the number above came off an accelerometer and nothing the model
   * says revises it. PRD §10 makes the advisory label a contract term for
   * anything measurement-adjacent, so it rides behind a trigger on the
   * deviation line and the bubble leads with the word - see `.heights__advice`.
   */
  advisory?: string | null;
}) {
  const deviation = Math.round((angle - target) * 10) / 10;
  const ok = Math.abs(deviation) <= tolerance;

  return (
    <div className="heights__meter">
      <AngleGauge angle={angle} target={target} tolerance={tolerance} />
      <p className={cn("heights__value tnum", ok ? "heights__value--ok" : "heights__value--off")}>
        {angle.toFixed(1)}°
      </p>
      <p className="heights__deviation tnum">
        {deviation === 0
          ? "Exactly on target"
          : `${deviation > 0 ? "+" : ""}${deviation}° from ${target}°`}
        {/* Behind a hint rather than set under the dial. The instrument's box
            is the *measurement* - an arc, a figure and how far off target it is
            - and a two-line sentence of model commentary inside it made the
            advice read as part of the reading, which is the one thing PRD §10
            says it may never do.

            An `i` rather than the spark, which is what the rest of this card
            hangs a sentence off - see `.checkitem__more`. The bubble carries
            the provenance in words instead, and it has to: "advisory" and "the
            analysis" are the two things PRD §10 wants said about this sentence,
            and with the mark gone there is nothing else on the card saying
            them. */}
        {advisory && (
          <InfoHint
            className="heights__advice"
            icon={<Info aria-hidden />}
            label="What the analysis suggests about this reading"
            text={`Advisory from the analysis: ${advisory}`}
          />
        )}
      </p>
    </div>
  );
}

function heightsView(
  assessment: AssessmentWithContext,
  config: Record<string, unknown>
): ModuleView {
  const metadata = (assessment.metadata ?? {}) as Record<string, unknown>;
  const target = Number(config.target_angle_deg ?? 75.5);
  const tolerance = Number(config.tolerance_deg ?? 2);
  const catalogue = checklistFrom(config, "checks");

  const angle = typeof metadata.angle_deg === "number" ? metadata.angle_deg : null;
  const checks = readAnswers(metadata.checks);

  // The photographs of the submitted attempt these ticks were taken against,
  // and `angle` is that submission's reading. Earlier attempts keep their own
  // photographs *and* their own angle, and reach the reader through the card's
  // history control - a re-shoot is usually a ladder that moved.
  const media = viewerItems(assessment.media, submittedAttempt(assessment));

  // Nothing was submitted at all: no reading, no answers, no photographs.
  // `plainView` says so in a sentence, which is the honest reading of a check
  // nobody has run yet. **A record missing only one of the three still renders**
  // - this used to be a single `heightsMetadata.safeParse`, so a check whose
  // reading had gone missing rendered as "Nothing was recorded against this
  // check" with its photographs, its five answers and three analyses sitting
  // right there on the row.
  if (angle === null && checks.length === 0 && media.length === 0) return plainView(assessment);

  const rows = checkRows(catalogue, checks, latestDetections(metadata));
  // Counted off `present`, not off "not missing": a check the catalogue lists
  // and nobody answered is neither confirmed nor failed, and rolling it into
  // the passed tally is the record claiming something it was never told.
  const passed = rows.filter((row) => row.answer?.present).length;

  return {
    title: title(assessment),
    // The one citation that is arithmetic rather than interpretation: the
    // target IS the 4:1 rule of OSHA 1926.1053 expressed as an angle, so
    // naming the standard here claims nothing the number does not already.
    subtitle: `Target ${target}° ±${tolerance}° · the 4:1 rule of OSHA 1926.1053`,
    // The angle is one of the five things this check asks, and it was the only
    // one this badge could see. The deviation is still on the card, in degrees,
    // under the instrument that measured it.
    actions: <StatusBadge assessment={assessment} />,
    bodyClass: "checkcard checkcard--heights",
    content: (
      <>
        <div className="checkcard__media heights__panel">
          {/* The reading, with the model's one sentence about it behind the
              spark on the deviation line - advice about a number it was handed,
              never a measurement of its own. The handset carries the same
              sentence in the same voice; it puts it under the card rather than
              behind a trigger, because there is no hover on a touch screen. */}
          {angle !== null ? (
            <AngleReading
              angle={angle}
              target={target}
              tolerance={tolerance}
              // The advice from the submission the reading came from, which is
              // the newest that has one - the same rule `composeMetadata` uses
              // to decide which angle is on the record. A sentence about the
              // angle beside a different submission's number is advice about a
              // set-up nobody stood next to.
              advisory={
                attemptHistory(metadata).find((entry) => entry.reading)?.angle_action ?? null
              }
            />
          ) : (
            <p className="heights__noangle">
              No angle was recorded against the submission on this record.
            </p>
          )}

          {/* Thumbnails, not a lead tile. The reading is the dial above; the
              photographs are what half of the checklist beside it was read
              from, and at 4:3 they were a second instrument of equal weight
              stacked under the first. Full size is one press away. */}
          <MediaGallery
            compact
            items={media}
            title={title(assessment)}
            placeholder={
              assessment.status === "completed"
                ? "No photographs were uploaded"
                : "Photographs appear here once the check is submitted"
            }
          />
        </div>

        <div className="checkcard__detail">
          <p className="checkcard__detail-label">
            Set-up checks
            <span className="checkcard__detail-count tnum">
              {passed} of {rows.length}
            </span>
          </p>

          <CheckList rows={rows} />
        </div>
      </>
    ),
  };
}

/* ─────────────────────────────────── PPE ───────────────────────────────── */

/**
 * The photographs on the left, the equipment list on the right.
 *
 * The tally is a heading over the list rather than a clause in the subtitle,
 * which is where the ladder card already puts it - the two sit side by side in
 * a row, and "6 of 7" belongs over the column it counts either way. The photo
 * count and the attempt number went with it: the strip says how many frames
 * there are by being that many frames, and which submission they are is what
 * the header's history control is for.
 */
function ppeView(assessment: AssessmentWithContext, config: Record<string, unknown>): ModuleView {
  const metadata = (assessment.metadata ?? {}) as Record<string, unknown>;
  const catalogue = checklistFrom(config, "items");
  const items = readAnswers(metadata.items);

  // The photographs of the submitted attempt this record's ticks were taken
  // against. Earlier attempts stay in `media.items` and get their own control
  // via `replacedAttempts`.
  const attempt = submittedAttempt(assessment);
  const media = viewerItems(assessment.media, attempt);

  // Read field by field rather than as one object, for the reason in
  // `heightsView`: a record that is missing one of them is still a record, and
  // an all-or-nothing parse renders the other two as "nothing was recorded".
  if (items.length === 0 && media.length === 0) return plainView(assessment);

  const rows = checkRows(catalogue, items, latestDetections(metadata));
  // Counted off `present`, not off "not missing": an item the catalogue lists
  // and nobody answered is neither confirmed nor missing, and rolling it into
  // the confirmed tally is the record claiming something it was never told.
  const confirmed = rows.filter((row) => row.answer?.present).length;

  return {
    title: title(assessment),
    subtitle: "Required equipment, confirmed on device from the photographs",
    // Which items are missing is the list below, row by row, with a tally over
    // it. Naming one of them up here made a card that badged "Boots missing"
    // beside a handset that badged the same check "Failed".
    actions: <StatusBadge assessment={assessment} />,
    bodyClass: "checkcard checkcard--ppe",
    content: (
      <>
        <div className="checkcard__media">
          {/* One shot cannot hold a hard hat and a pair of boots, so a card
              showing one of five is a card showing most of the evidence as
              absent. Every frame of the set is on the strip. */}
          <MediaGallery
            items={media}
            title={title(assessment)}
            placeholder={
              assessment.status === "completed"
                ? "No photographs were uploaded"
                : "Photographs appear here once the check is submitted"
            }
          />
        </div>

        <div className="checkcard__detail">
          <p className="checkcard__detail-label">
            Protective equipment
            <span className="checkcard__detail-count tnum">
              {confirmed} of {rows.length}
            </span>
          </p>

          <CheckList rows={rows} split />
        </div>
      </>
    ),
  };
}

/* ─────────────────────────────── utility ───────────────────────────────── */

/**
 * Advisory, and it says so. PRD §10 makes the distinction a contract term:
 * nothing here may read as a detection, so this lists what the technician
 * reported seeing and nothing about what anything found.
 */
function utilityView(assessment: AssessmentWithContext): ModuleView {
  const parsed = utilityMetadata.safeParse(assessment.metadata);
  if (!parsed.success) return plainView(assessment);

  const { fixtures, acknowledged } = parsed.data;

  return {
    title: title(assessment),
    subtitle: "Reported by the technician. Not a detection.",
    actions: <StatusBadge assessment={assessment} />,
    bodyClass: "utility",
    content: (
      <>
        <p className="utility__summary">
          {fixtures.length === 0
            ? "Nothing reported near the work."
            : `${fixtures.length} fixture${fixtures.length === 1 ? "" : "s"} reported near the work`}
        </p>

        {fixtures.length > 0 && (
          <ul className="utility__chips">
            {fixtures.map((fixture) => (
              <li key={fixture} className="utility__chip">
                <Zap aria-hidden />
                {fixture}
              </li>
            ))}
          </ul>
        )}

        {/* The switch the technician answered, read back as a sentence with a
            state - never colour alone. */}
        <p className={cn("utility__ack", acknowledged ? "utility__ack--ok" : "utility__ack--off")}>
          {acknowledged ? <CircleCheck aria-hidden /> : <CircleX aria-hidden />}
          {acknowledged
            ? "Checked for buried and live services before work started"
            : "Did not confirm checking for buried and live services"}
        </p>
      </>
    ),
  };
}

/* ─────────────────────── anything else in the catalogue ─────────────────── */

function plainView(assessment: AssessmentWithContext): ModuleView {
  return {
    title: title(assessment),
    subtitle: assessment.module?.advisory ? "Advisory. Not a detection." : undefined,
    actions: <StatusBadge assessment={assessment} />,
    content: assessment.notes ? (
      <p>{assessment.notes}</p>
    ) : (
      <p className="muted">Nothing was recorded against this check.</p>
    ),
  };
}
